import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

export default function CoextrusionPrintView() {
  const { productionOrderId } = useParams<{ productionOrderId: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await fetch(`/api/coextrusion-orders/${productionOrderId}`, config);
      const d = await res.json();
      setData(d);
      // Fetch company logo
      const tenantSlug = localStorage.getItem('tenantSlug') || 'demo-corp';
      try {
        const logoRes = await axios.get(`/api/tenants/${tenantSlug}/logo`, config);
        if (logoRes.data?.logoUrl) setLogoUrl(logoRes.data.logoUrl);
      } catch {}
      setLoading(false);
    };
    load();
  }, [productionOrderId]);

  useEffect(() => {
    if (!loading && data) {
      setTimeout(() => window.print(), 400);
    }
  }, [loading, data]);

  if (loading || !data) return <div className="p-8 text-center">Cargando...</div>;

  const order = data.order;
  const fd = data.existing?.formData || {};
  const totalKg = parseFloat(order?.quantityPlanned || '0');

  const caraTratada: any[] = fd.caraTratada || [];
  const caraSello: any[] = fd.caraSello || [];

  const totalPct = (rows: any[]) => rows.reduce((s, r) => s + (parseFloat(r.pctResina) || 0), 0).toFixed(2);
  const totalCant = (rows: any[]) => rows.reduce((s, r) => s + (parseFloat(r.cantidad) || 0), 0).toFixed(2);

  return (
    <div className="print-container p-6 font-sans text-black bg-white max-w-[210mm] mx-auto">
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 10mm; }
          body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          .no-print { display: none !important; }
        }
        body { font-family: Arial, sans-serif; font-size: 11px; }
        table { border-collapse: collapse; }
      `}</style>

      {/* Print button */}
      <div className="no-print mb-4 flex gap-2">
        <button onClick={() => window.print()}
          className="bg-blue-700 text-white px-4 py-2 rounded text-sm hover:bg-blue-800">
          🖨️ Imprimir
        </button>
        <button onClick={() => window.close()}
          className="bg-gray-500 text-white px-4 py-2 rounded text-sm hover:bg-gray-600">
          Cerrar
        </button>
      </div>

      {/* ─── Document header ─── */}
      <table className="w-full border border-gray-700 mb-2" style={{ tableLayout: 'fixed' }}>
        <tbody>
          <tr>
            <td className="border border-gray-700 px-3 py-2 w-32 align-middle text-center">
              {logoUrl
                ? <img src={logoUrl} alt="Logo" className="max-h-14 max-w-[110px] object-contain mx-auto" />
                : <div className="font-bold text-sm text-blue-800">🏭 Rototec</div>
              }
            </td>
            <td className="border border-gray-700 px-3 py-2 text-center align-middle">
              <div className="text-base font-bold uppercase tracking-wider">ORDEN DE CO-EXTRUSIÓN</div>
            </td>
            <td className="border border-gray-700 px-2 py-1 w-36 text-xs">
              <div className="flex justify-between"><span className="font-bold">CÓDIGO:</span><span>4-PROC0-049</span></div>
              <div className="flex justify-between"><span className="font-bold">EDICIÓN:</span><span>0-270625</span></div>
            </td>
          </tr>
          <tr>
            <td colSpan={3} className="border border-gray-700 px-3 py-1 text-right">
              <span className="font-bold mr-2">LOTE:</span>
              <span className="font-semibold text-blue-900">{order?.lotCode || ''}</span>
            </td>
          </tr>
        </tbody>
      </table>

      {/* ─── Main content: formula name + fields ─── */}
      <table className="w-full border border-gray-700 mb-2 text-xs" style={{ tableLayout: 'fixed' }}>
        <tbody>
          <tr>
            <td colSpan={2} className="border border-gray-700 px-2 py-1 font-bold text-sm uppercase">
              FORMULA: {fd.formulaName || ''}
            </td>
            <td className="border border-gray-700 px-2 py-1 font-bold" rowSpan={6}>
              <div className="space-y-1">
                <div className="flex justify-between gap-2">
                  <span className="font-bold">ANCHO:</span>
                  <span>{fd.ancho || ''} CM</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="font-bold">CALIBRE:</span>
                  <span>{fd.calibre || ''} GAUGES</span>
                </div>
                <div className="flex justify-between gap-2 border-t pt-1">
                  <span className="font-bold">O. PRODUCCIÓN:</span>
                  <span>{totalKg.toFixed(2)} Kg</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="font-bold">INVENTARIO:</span>
                  <span>{fd.inventario || '0'} Kg</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="font-bold">FECHA DE ENTREGA:</span>
                  <span>{order?.dueDate ? new Date(order.dueDate).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: '2-digit' }).toUpperCase() : ''}</span>
                </div>
              </div>
            </td>
          </tr>
          <tr>
            <td className="border border-gray-700 px-2 py-0.5 font-bold w-24">CLIENTE:</td>
            <td className="border border-gray-700 px-2 py-0.5">{order?.customerName || ''}</td>
          </tr>
          <tr>
            <td className="border border-gray-700 px-2 py-0.5 font-bold">PRODUCTO:</td>
            <td className="border border-gray-700 px-2 py-0.5">{order?.productName || ''}</td>
          </tr>
          <tr>
            <td className="border border-gray-700 px-2 py-0.5 font-bold">COLOR:</td>
            <td className="border border-gray-700 px-2 py-0.5">{fd.color || ''}</td>
          </tr>
          <tr>
            <td className="border border-gray-700 px-2 py-0.5 font-bold" colSpan={2}>
              ORDEN DE PRODUCCIÓN INTERNA:
              <span className="ml-2 font-normal">{order?.orderNumber || ''}</span>
            </td>
          </tr>
          {data.linkedOrders && data.linkedOrders.length > 0 && (
            <tr>
              <td className="border border-gray-700 px-2 py-0.5 font-bold" colSpan={2}>
                OP(S) VINCULADA(S):
                <span className="ml-2 font-normal">{data.linkedOrders.map((o: any) => o.orderNumber).join(', ')}</span>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* ─── Resumen (ROT-182: this used to be a right-side column sitting
          next to CARA TRATADA only, which squeezed that table to flex-1
          while CARA DE SELLO stayed full-width below — the two tables'
          columns never lined up. Then a full-width row between them, so
          both tables share the same width and align. ROT-182 follow-up:
          moved above both faces instead of sitting between them, since
          it summarizes the whole structure, not just CARA TRATADA. ─── */}
      <table className="w-full border border-gray-700 mb-2 text-xs text-center" style={{ tableLayout: 'fixed' }}>
        <tbody>
          <tr>
            <td className="border border-gray-700 px-1 py-1">
              <div className="font-bold">FACTOR DESPERDICIO</div>
              <div className="text-sm font-bold">{fd.factorDesperdicio || ''}%</div>
            </td>
            <td className="border border-gray-700 px-1 py-1">
              <div className="font-bold">% CARA TRATADA</div>
              <div className="text-sm font-bold">{fd.porcentajeCaraTratada || ''}%</div>
            </td>
            <td className="border border-gray-700 px-1 py-1">
              <div className="font-bold">% CARA DE SELLO</div>
              <div className="text-sm font-bold">{fd.porcentajeCaraSello || ''}%</div>
            </td>
            <td className="border border-gray-700 px-1 py-1">
              <div className="font-bold">PESO ESTRUCTURA</div>
              <div>{fd.pesoEstructura || ''} <span className="text-gray-500">gr/m²</span></div>
            </td>
            <td className="border border-gray-700 px-1 py-1">
              <div className="font-bold">RENDIMIENTO</div>
              <div>{fd.rendimiento || ''} <span className="text-gray-500">m²/kg</span></div>
            </td>
            <td className="border border-gray-700 px-1 py-1">
              <div className="font-bold">MICRAJE</div>
              <div>{fd.micraje || ''} <span className="text-gray-500">Micras</span></div>
            </td>
            <td className="border border-gray-700 px-1 py-1">
              <div className="font-bold">DESPERDICIO</div>
              <div>{fd.desperdicio || ''} <span className="text-gray-500">Kg</span></div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* ─── CARA TRATADA ─── */}
      <div className="mb-2">
        <div className="bg-gray-200 border border-gray-700 text-center font-bold py-0.5 text-xs uppercase">CARA TRATADA</div>
        <table className="w-full border border-gray-700 text-xs">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-600 px-1 py-0.5 text-left">MATERIA PRIMA</th>
              <th className="border border-gray-600 px-1 py-0.5 text-center">% formulación</th>
              <th className="border border-gray-600 px-1 py-0.5 text-center">% de resina</th>
              <th className="border border-gray-600 px-1 py-0.5 text-center underline">CANTIDAD</th>
            </tr>
          </thead>
          <tbody>
            {caraTratada.filter(r => r.materiaPrima || r.cantidad).map((row, i) => (
              <tr key={i}>
                <td className="border border-gray-600 px-1 py-0.5">{row.materiaPrima}</td>
                <td className="border border-gray-600 px-1 py-0.5 text-center">{row.pctFormulacion ? `${row.pctFormulacion}%` : ''}</td>
                <td className="border border-gray-600 px-1 py-0.5 text-center">{row.pctResina ? `${row.pctResina}%` : ''}</td>
                <td className="border border-gray-600 px-1 py-0.5 text-right">{row.cantidad ? `${row.cantidad} Kg` : ''}</td>
              </tr>
            ))}
            <tr className="bg-gray-100 font-bold">
              <td className="border border-gray-600 px-1 py-0.5 text-right" colSpan={2}>100%</td>
              <td className="border border-gray-600 px-1 py-0.5 text-center">{totalPct(caraTratada)}%</td>
              <td className="border border-gray-600 px-1 py-0.5 text-right">{totalCant(caraTratada)} Kg</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ─── CARA DE SELLO ─── */}
      <div className="mb-2">
        <div className="bg-gray-200 border border-gray-700 text-center font-bold py-0.5 text-xs uppercase">CARA DE SELLO</div>
        <table className="w-full border border-gray-700 text-xs">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-600 px-1 py-0.5 text-left">MATERIA PRIMA</th>
              <th className="border border-gray-600 px-1 py-0.5 text-center">% formulación</th>
              <th className="border border-gray-600 px-1 py-0.5 text-center">% de resina</th>
              <th className="border border-gray-600 px-1 py-0.5 text-center underline">CANTIDAD</th>
            </tr>
          </thead>
          <tbody>
            {caraSello.filter(r => r.materiaPrima || r.cantidad).map((row, i) => (
              <tr key={i}>
                <td className="border border-gray-600 px-1 py-0.5">{row.materiaPrima}</td>
                <td className="border border-gray-600 px-1 py-0.5 text-center">{row.pctFormulacion ? `${row.pctFormulacion}%` : ''}</td>
                <td className="border border-gray-600 px-1 py-0.5 text-center">{row.pctResina ? `${row.pctResina}%` : ''}</td>
                <td className="border border-gray-600 px-1 py-0.5 text-right">{row.cantidad ? `${row.cantidad} Kg` : ''}</td>
              </tr>
            ))}
            <tr className="bg-gray-100 font-bold">
              <td className="border border-gray-600 px-1 py-0.5 text-right" colSpan={2}>100%</td>
              <td className="border border-gray-600 px-1 py-0.5 text-center">{totalPct(caraSello)}%</td>
              <td className="border border-gray-600 px-1 py-0.5 text-right">{totalCant(caraSello)} Kg</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ─── Footer ─── */}
      <table className="w-full border border-gray-700 mb-2 text-xs">
        <tbody>
          <tr>
            <td className="border border-gray-700 px-2 py-1">
              <label className="flex items-center gap-1">
                <input type="checkbox" checked={!!fd.corteLateral} readOnly className="w-3 h-3" />
                <span className="font-bold">CORTE LATERAL</span>
              </label>
            </td>
            <td className="border border-gray-700 px-2 py-1">
              <label className="flex items-center gap-1">
                <input type="checkbox" checked={!!fd.refilado} readOnly className="w-3 h-3" />
                <span className="font-bold">REFILADO</span>
              </label>
              {fd.refiladoCms && <span className="ml-2">{fd.refiladoCms} CMS.</span>}
            </td>
            <td className="border border-gray-700 px-2 py-1">
              <label className="flex items-center gap-1">
                <input type="checkbox" checked={!!fd.tubular} readOnly className="w-3 h-3" />
                <span className="font-bold">TUBULAR</span>
              </label>
            </td>
          </tr>
          <tr>
            <td className="border border-gray-700 px-2 py-1">
              <span className="font-bold">PESO DE BOBINA: </span>{fd.pesoBobina || ''} Kg
            </td>
            <td className="border border-gray-700 px-2 py-1">
              <span className="font-bold">DIÁMETRO BOBINA: </span>{fd.diametroBobina || ''}
            </td>
            <td className="border border-gray-700 px-2 py-1">
              <span className="font-bold">METROS: </span>{fd.metros || ''}
            </td>
          </tr>
          <tr>
            <td colSpan={3} className="border border-gray-700 px-2 py-1">
              <span className="font-bold">OBSERVACIONES: </span>{fd.observaciones || ''}
            </td>
          </tr>
        </tbody>
      </table>

      {/* ─── Signatures ─── */}
      <table className="w-full border border-gray-700 text-xs">
        <tbody>
          <tr>
            <td className="border border-gray-700 px-3 py-6 text-center w-1/2">
              <div className="border-t border-gray-700 pt-1 font-bold">Autorizado por: GERENCIA GENERAL</div>
            </td>
            <td className="border border-gray-700 px-3 py-6 text-center w-1/2">
              <div className="border-t border-gray-700 pt-1 font-bold">ÁREA EXTRUSIÓN</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
